import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Phone } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../ui/sheet';
import { useSettings } from '../../context/SettingsContext';
import Logo from '../Logo';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { businessInfo } = useSettings();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="sticky top-0 z-50 bg-[var(--bg-primary)]/95 backdrop-blur-sm border-b border-[var(--border-light)]">
      <div className="container">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="hover:opacity-80 transition-opacity" aria-label="HomeGlow Nails Home">
            <Logo size="small" className="sm:hidden" />
            <Logo size="default" className="hidden sm:block" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative py-2 text-sm transition-colors ${
                  isActive(link.path) 
                    ? 'text-[var(--text-heading)] font-medium' 
                    : 'text-[var(--text-secondary)] hover:text-[var(--text-heading)]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a 
              href={`tel:${businessInfo.phone}`}
              className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-heading)] transition-colors"
            >
              <Phone size={16} />
              <span className="hidden xl:inline">{businessInfo.phone}</span>
            </a>
            <Link to="/book" className="btn-primary !min-w-0 !px-6">
              Book Now
            </Link>
          </div>

          {/* Mobile: Phone + Menu */}
          <div className="flex items-center gap-1 lg:hidden">
            <a 
              href={`tel:${businessInfo.phone}`}
              className="p-3 text-[var(--text-secondary)] hover:text-[var(--text-heading)] transition-colors rounded-full hover:bg-[var(--bg-subtle)]"
              aria-label="Call us"
            >
              <Phone size={20} />
            </a>
            
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <button 
                  className="p-3 rounded-full hover:bg-[var(--bg-subtle)] transition-colors" 
                  aria-label="Open menu"
                >
                  <Menu size={22} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full max-w-sm bg-[var(--bg-primary)] p-0 rounded-l-2xl">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex flex-col h-full">
                  {/* Mobile Logo */}
                  <div className="p-5 border-b border-[var(--border-light)]">
                    <Logo size="small" />
                  </div>
                  
                  {/* Mobile Nav */}
                  <nav className="flex-1 overflow-y-auto py-4">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setIsOpen(false)}
                        className={`block px-5 py-4 text-lg transition-colors ${
                          isActive(link.path) 
                            ? 'text-[var(--text-heading)] font-medium bg-[var(--bg-subtle)]' 
                            : 'text-[var(--text-secondary)]'
                        }`}
                        style={{fontFamily: "'Cormorant Garamond', serif"}}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </nav>
                  
                  {/* Mobile CTA */}
                  <div className="p-5 border-t border-[var(--border-light)] space-y-4">
                    <a 
                      href={`tel:${businessInfo.phone}`}
                      className="flex items-center gap-3 text-[var(--text-secondary)] py-2"
                    >
                      <Phone size={18} />
                      <span>{businessInfo.phone}</span>
                    </a>
                    <Link 
                      to="/book" 
                      onClick={() => setIsOpen(false)}
                      className="btn-primary w-full"
                    >
                      Book Now
                    </Link>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
